import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useTheme,
} from '@chakra-ui/react';
import {
  AppTooltip,
  CFR_CHART_KEYS,
  COMMIT_CHART_KEYS,
  CUSTOM_CHART_KEYS,
  CircleCheck,
  ConfigureColumns,
  ContributorList,
  ContributorSettingsIcon,
  ContributorTableCompareIcon,
  ContributorTableFilters,
  CustomSelect,
  DEPLOY_CHART_KEYS,
  DangerIcon,
  DataGrid,
  DownloadButton,
  DownloadIcon,
  Filter,
  GOAL_METRIC_GIT_KEYS,
  GOAL_METRIC_JIRA_KEYS,
  ISSUE_CHART_KEYS,
  ISelect,
  InfoIcon,
  JobRoleDto,
  Loader,
  MEETING_CHARTS,
  MetricSidebar,
  MetricTooltip,
  PR_CHART_KEYS,
  Paginate,
  PeriodType,
  RepoDto,
  SearchInputBar,
  SlideBar,
  eliminateHtmlTags,
  getActivityFilters,
  getContributorActivityFilterLabel,
  getContributorActivityFilterOptions,
  splitTime,
  useContributorTableStore,
  useDashboardStore,
  useQueryState,
  useToastHook,
} from '@devd-client/devd/components';
import { Link, useLocation } from 'react-router-dom';
import {
  useContributorTableCsvMutation,
  useContributorTableMetrics,
  useSaveContributorTableMetricsMutationForTeams,
} from '../../../api';
import { ChakraStylesConfig, Select } from 'chakra-react-select';
import { useContributorTable } from '../../../hooks/useContributorTable';
import { useEffect, useMemo, useState } from 'react';

interface ContributorsProps {
  selected: PeriodType;
  selectedTeam: ISelect;
}

const chakraStyles: ChakraStylesConfig = {
  dropdownIndicator: (provided, state) => ({
    ...provided,
    background: 'transparent',
    outline: 'none',
    border: 'none',
    padding: 0,
    mr: 2,
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    // pl: 2,
    mr: 5,
    w: 'full',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: 'black',
  }),
  container: (provided, state) => {
    return {
      ...provided,
      cursor: 'pointer',
      minW: 150,
      maxW: 350,
      w: 'fit-content',
      zIndex: 99,
    };
  },
  option: (provided, state) => {
    return {
      ...provided,
      _hover: { background: state.isSelected ? '' : 'grey.100' },
      overflowX: 'hidden',
    };
  },
  menuList: (provided, state) => {
    return {
      ...provided,
      scrollbarWidth: 'none',
    };
  },
};

const Contributors = ({ selected }: ContributorsProps) => {
  const {
    searchTerm,
    setSearchTerm,
    repoState,
    setRepoState,
    repoList,
    languageState,
    setLanguageState,
    languagesList,
    selectedMetrics,
    setSelectedMetrics,
    orderBy,
    orderColumn,
    setOrderBy,
    setOrderColumn,
    compareTo,
    setCompareTo,
    selectedPermanentColumns,
    setSelectedPermanentColumns,
    metricFilterState,
    setMetricFilterState,
    showContributorPercentilesState,
    setShowContributorPercentilesState,
    jobRoleState,
    setJobRoleState,
    jobRoleList,
  } = useContributorTable();
  const { colors } = useTheme();
  const { pathname } = useLocation();
  const [newToast] = useToastHook();
  const configureMetricsModal = useDisclosure();
  const { setSelectedContributors, setFilteredContributors } =
    useContributorTableStore();
  const { data } = useContributorTableMetrics();
  const { mutate: postCsv } = useContributorTableCsvMutation();
  const totalActiveFilters =
    repoState.length +
    languageState.length +
    jobRoleState.length +
    metricFilterState.length +
    (compareTo ? 1 : 0) +
    (showContributorPercentilesState ? 1 : 0);

  const {
    mutate: saveContributorTableMetricsForTeams,
    isLoading: saveContributorTableMetricsForTeamsLoading,
  } = useSaveContributorTableMetricsMutationForTeams();

  const slideBar = useDisclosure();

  const [activityFilter, setActivityFilter] = useQueryState('activity');
  const { setSelectedTeam } = useDashboardStore();

  const team = useMemo(() => {
    return [decodeURIComponent(pathname.split('/')[2])];
  }, [pathname]);

  const [activityType, setActivityType] = useState({
    value: activityFilter || 'all',
    label: getContributorActivityFilterLabel(
      activityFilter || 'all',
      selected.startDate,
      selected.endDate
    ),
  });

  // on unmounting of component. reset to default filters
  useEffect(() => {
    return () => {
      setSelectedContributors([]);
      setFilteredContributors([]);
      setSearchTerm('');
    };
  }, []);

  const memoizedRepoState = useMemo(() => {
    return repoState?.length > 0
      ? repoState?.map((el: ISelect) => el.value)
      : [];
  }, [repoState]);

  const memoizedLanguageState = useMemo(() => {
    return languageState?.length > 0
      ? languageState?.map((el: ISelect) => el.value)
      : [];
  }, [languageState]);

  const memoizedJobRoleState = useMemo(() => {
    return jobRoleState?.length > 0
      ? jobRoleState?.map((el: ISelect) => el.value)
      : [];
  }, [jobRoleState]);

  const memoizedActivityFilter = useMemo(() => {
    return getActivityFilters(activityFilter || 'all');
  }, [activityFilter]);

  useEffect(() => {
    setActivityType({
      value: activityFilter || 'all',
      label: getContributorActivityFilterLabel(
        activityFilter || 'all',
        selected.startDate,
        selected.endDate
      ),
    });
  }, [activityFilter, selected.startDate, selected.endDate]);

  const dynamicFilterOptions = useMemo(
    () =>
      getContributorActivityFilterOptions(
        selected.startDate,
        selected.endDate,
        false
      ),
    [selected.startDate, selected.endDate]
  );

  const repoOptions =
    repoList?.dto?.map((repo: RepoDto) => ({
      value: repo.key,
      label: repo.name,
    })) || [];

  const languageOptions =
    languagesList?.dto?.map((language: string) => ({
      value: language,
      label: language,
    })) || [];

  const jobRoleOptions =
    jobRoleList?.dto?.map((jobRole: JobRoleDto) => ({
      value: jobRole.role,
      label: jobRole.role,
    })) || [];

  const handleActivityFilterChange = (selectedFilter: string) => {
    const newFilter = {
      value: selectedFilter,
      label: selectedFilter
        ? getContributorActivityFilterLabel(
            selectedFilter,
            selected.startDate,
            selected.endDate
          )
        : 'All',
    };
    setActivityType(newFilter);
    setActivityFilter(selectedFilter);
  };

  return (
    <Box>
      <Flex align="start" mt={1} gap={2} flexWrap={'wrap'}>
        <Flex align={'center'} w={'100%'}>
          <Flex align={'center'} gap={1}>
            <Box width={'100%'} alignSelf={'end'} maxWidth={'200px'}>
              <SearchInputBar
                includeIcon
                searchValue={searchTerm}
                setSearchValue={setSearchTerm}
                placeholder="Search Contributors"
                size={'sm'}
                width={'200px'}
              />
            </Box>

            <Tooltip hasArrow label="Reset Table">
              <IconButton
                icon={<CircleCheck color={colors.text.secondary} />}
                aria-label="Reset filters"
                justifySelf={'self-end'}
                alignSelf={'flex-end'}
                borderRadius={'4px'}
                size="sm"
                variant={'outline'}
                border={'1px'}
                borderColor={'#E5E5E5'}
                onClick={() => {
                  setSelectedContributors([]);
                  setLanguageState([]);
                  setRepoState([]);
                  setJobRoleState([]);
                  setSearchTerm('');
                  setCompareTo('');
                  setOrderBy('');
                  setOrderColumn('');
                  setCompareTo('');
                  handleActivityFilterChange('all');
                  setMetricFilterState([]);
                  setFilteredContributors([]);
                }}
                cursor={'pointer'}
              />
            </Tooltip>
          </Flex>

          <Flex
            align="center"
            gap={1}
            alignSelf={'end'}
            ml={'auto'}
            justify={'center'}
          >
            {/* <DownloadButton downloadCSV={handleContributorTableCsvDownload} /> */}
            <Button
              bg="transparent"
              border="1px solid #E0E0E0"
              borderRadius="4px"
              color="text.secondary2"
              size="sm"
              fontWeight="normal"
              onClick={slideBar.onOpen}
              leftIcon={<Filter width={16} height={16} />}
              display={'flex'}
              alignItems={'center'}
            >
              Filter By
              {totalActiveFilters > 0 && (
                <Flex
                  bg={colors.primary}
                  color={'white'}
                  borderRadius={'full'}
                  justify={'center'}
                  align={'center'}
                  pr={'1px'}
                  w={'16px'}
                  h={'16px'}
                  ml={1}
                  fontSize={'xs'}
                  fontWeight={'bold'}
                >
                  {totalActiveFilters}
                </Flex>
              )}
            </Button>
            <Button
              bg="transparent"
              border="1px solid #E0E0E0"
              borderRadius="4px"
              color="text.secondary2"
              size="sm"
              fontWeight="normal"
              onClick={configureMetricsModal.onOpen}
              leftIcon={<ContributorSettingsIcon />}
            >
              Configure Columns
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {configureMetricsModal.isOpen && (
        <ConfigureColumns
          {...configureMetricsModal}
          componentOrigin="team-details"
          team={decodeURIComponent(pathname.split('/')[2])}
          metricList={[
            ...(data?.dto || []).map((metric: MetricTooltip) => {
              return {
                metricName: metric?.title ?? '',
                metricKey: metric?.metric,
                tabs: metric?.tags,
                tooltip: metric?.info,
              };
            }),
            {
              metricName: 'Goals',
              metricKey: 'GOALS',
              tabs: ['Goals'],
              tooltip:
                'Goals are the main objectives that contributors aim to achieve. They are typically set for a specific period and are used to track progress and measure success.',
            },
            {
              metricName: 'Key Results',
              metricKey: 'KEY_RESULTS',
              tabs: ['Goals'],
              tooltip:
                "Measures how often a Key Result's target was met within its timeframe. E.g., if a KR met its target in 4 out of 5 months, its success is 80%. This shows the consistency of achieving a specific outcome. Goal/KR Progress levels (On Track, Behind, At Risk) are based on org-wide settings.",
            },
          ]}
          selectedMetrics={selectedMetrics}
          setSelectedMetrics={setSelectedMetrics}
          selectedPermanentColumns={selectedPermanentColumns}
          setSelectedPermanentColumns={setSelectedPermanentColumns}
          permanentColumns={[
            'Contributors',
            'Teams',
            'Languages',
            'Knowledge Area',
            'Team Allocation',
          ]}
          defaultMetrics={['PR_CYCLE_TIME', 'ACTIVE_DAYS']}
          saveColumns={saveContributorTableMetricsForTeams}
          saveColumnsIsLoading={saveContributorTableMetricsForTeamsLoading}
        />
      )}

      {slideBar.isOpen && (
        <SlideBar
          openDrawer={slideBar.isOpen}
          SideDrawerBodyHeight={false}
          bodyHeight="85vh"
          isModalOpen={configureMetricsModal.isOpen}
          width={'420px'}
          handleDrawerClose={slideBar.onClose}
          sideBarHeading="Filters"
          sideBarSubHeading={<></>}
          content={
            <ContributorTableFilters
              configureMetricsModalOpen={configureMetricsModal.onOpen}
              slideBarOnClose={slideBar.onClose}
              metricsList={data}
              selectedMetrics={selectedMetrics}
              metricFilterState={metricFilterState}
              setMetricFilterState={setMetricFilterState}
              repoOptions={repoOptions}
              languageOptions={languageOptions}
              jobRoleOptions={jobRoleOptions}
              jobRoleState={jobRoleState}
              setJobRoleState={setJobRoleState}
              dynamicFilterOptions={dynamicFilterOptions}
              activityType={activityType}
              handleActivityFilterChange={handleActivityFilterChange}
              languageState={languageState}
              setLanguageState={setLanguageState}
              repoState={repoState}
              setRepoState={setRepoState}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              compareTo={compareTo}
              setCompareTo={setCompareTo}
              setOrderBy={setOrderBy}
              setOrderColumn={setOrderColumn}
              setSelectedContributors={setSelectedContributors}
              showContributorPercentiles={showContributorPercentilesState}
              setShowContributorPercentiles={setShowContributorPercentilesState}
            />
          }
        />
      )}

      <ContributorList
        height="70vh"
        metricTooltips={
          data?.dto?.filter((metricItem: MetricTooltip) =>
            selectedMetrics
              .map((item) => item.metricKey)
              .includes(metricItem?.metric)
          ) || []
        }
        metricList={data?.dto}
        compareTo={compareTo}
        searchTerm={searchTerm}
        hiddenColumns={[
          'Teams',
          'Languages',
          'Knowledge Area',
          'Team Allocation',
        ].filter((item) => !selectedPermanentColumns.includes(item))}
        repoState={memoizedRepoState}
        languageState={memoizedLanguageState}
        jobRoleState={memoizedJobRoleState}
        activityFilter={memoizedActivityFilter}
        selectedMetrics={selectedMetrics}
        orderBy={orderBy ?? ''}
        setOrderBy={setOrderBy}
        orderColumn={orderColumn ?? ''}
        setOrderColumn={setOrderColumn}
        startDate={selected.startDate}
        endDate={selected.endDate}
        teamState={team}
        metricFilterState={metricFilterState}
        showContributorPercentiles={showContributorPercentilesState}
      />
    </Box>
  );
};

export default Contributors;
